import _ from 'lodash';

import { initialPagination, initialState, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';
import { isPaidPayment } from '@/pages/configurations/billing-subscription-payments/utils';

import {
  isOpenAssignmentStatus,
  readNextCharge,
  toAgreementWriteRequest,
  toAssignmentRequest,
  unwrapList,
} from './agreementPayload';
import { API_ROUTE, MODEL_NAME, PAYMENTS_HISTORY_ROUTE } from './constants';

const api = bindServices(MODEL_NAME);

const emptySubscriptionView = {
  subscriptionId: null,
  loading: false,
  assignments: [],
  nextCharge: null,
  payments: [],
  error: null,
  paymentsError: null,
};

const moduleInitialState = {
  ...initialState,
  list: [],
  orderBy: null,
  select: null,
  searchSelect: null,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  filtersConfig: [],
  activeOnly: true,
  deactivateModal: { state: false, id: null, name: null, submitting: false },
  assignModal: {
    state: false,
    submitting: false,
    subscriptionId: null,
    companyName: null,
    openAssignment: null,
  },
  endModal: { state: false, id: null, submitting: false },
  subscriptionView: emptySubscriptionView,
};

const notifyFormError = function* (put, error) {
  if (!error?.message) return;
  if (error.validationErrors && _.isEmpty(error.validationErrors[0]?.field)) {
    _.set(error, 'message', error.validationErrors[0].errorMessages[0]);
  }
  yield put({
    type: 'global/showNotificationsForm',
    payload: { visible: true, type: 'error', message: error.message },
  });
  yield put({
    type: 'errors',
    payload: { validationErrors: error.validationErrors || [], message: error.message },
  });
};

const CustomerDiscountsModel = {
  namespace: MODEL_NAME,
  state: moduleInitialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const activeOnly = yield select((state) => state[MODEL_NAME].activeOnly);
        const name = payload?.search;
        const res = yield call(api.getAll, {
          route: `${API_ROUTE}/agreements`,
          // toQuery drops plain keys. activeOnly and name must bypass it.
          passthroughParams: {
            activeOnly: activeOnly !== false,
            ...(name ? { name } : {}),
          },
          readPriority: true,
        });
        const items = unwrapList(res);
        yield put({
          type: 'queryData',
          payload: {
            items,
            limit: Math.max(items.length, 1),
            page: 1,
            totalCount: items.length,
          },
        });
      } catch (e) {
        yield put({
          type: 'queryData',
          payload: { items: [], limit: 1, page: 1, totalCount: 0 },
        });
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        if (!payload?.id) return;
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.getById, {
          route: `${API_ROUTE}/agreements`,
          id: payload.id,
          readPriority: true,
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /* errorHandler already surfaces the API message */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, {
          route: `${API_ROUTE}/agreements`,
          data: toAgreementWriteRequest(payload.data),
        });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        yield* notifyFormError(put, e);
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        const id = payload.data?.id;
        if (!id) return;
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.apiRoute, {
          route: `${API_ROUTE}/agreements/${id}`,
          method: 'put',
          data: toAgreementWriteRequest(payload.data),
        });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        yield* notifyFormError(put, e);
      }
    },

    *setIncludeInactive({ payload }, { put, select }) {
      yield put({ type: 'applyActiveOnly', payload: { activeOnly: !payload } });
      const searchCriteria = yield select((state) => state.global.searchCriteria);
      yield put({ type: 'fetchList', payload: { search: searchCriteria || null } });
    },

    *openDeactivate({ payload }, { put }) {
      yield put({
        type: 'setDeactivateModal',
        payload: { state: true, id: payload?.id, name: payload?.name, submitting: false },
      });
    },

    *closeDeactivate(_, { put }) {
      yield put({
        type: 'setDeactivateModal',
        payload: { state: false, id: null, name: null, submitting: false },
      });
    },

    *deactivateAgreement({ payload }, { call, put }) {
      const id = payload?.id;
      if (!id) return;
      yield put({ type: 'setDeactivateModal', payload: { submitting: true } });
      try {
        yield call(api.apiRoute, {
          route: `${API_ROUTE}/agreements/${id}/deactivate`,
          method: 'post',
        });
        yield put({
          type: 'setDeactivateModal',
          payload: { state: false, id: null, name: null, submitting: false },
        });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        yield put({ type: 'setDeactivateModal', payload: { submitting: false } });
      }
    },

    *openAssign({ payload }, { put }) {
      yield put({
        type: 'setAssignModal',
        payload: {
          state: true,
          submitting: false,
          subscriptionId: payload?.subscriptionId || null,
          companyName: payload?.companyName || null,
          openAssignment: null,
        },
      });
    },

    *closeAssign(_, { put }) {
      yield put({ type: 'resetAssignModal' });
    },

    *previewAssignment({ payload }, { call, put }) {
      const subscriptionId = payload?.subscriptionId;
      if (!subscriptionId) {
        yield put({ type: 'setAssignModal', payload: { openAssignment: null } });
        return;
      }
      try {
        const res = yield call(api.getAll, {
          route: `${API_ROUTE}/subscriptions/${subscriptionId}/assignments`,
          readPriority: true,
        });
        const openAssignment =
          unwrapList(res).find((row) => isOpenAssignmentStatus(row?.status)) || null;
        yield put({ type: 'setAssignModal', payload: { openAssignment } });
      } catch (e) {
        /* errorHandler already surfaces the API message */
      }
    },

    *assignDiscount({ payload }, { call, put, select }) {
      yield put({ type: 'setAssignModal', payload: { submitting: true } });
      try {
        yield call(api.apiRoute, {
          route: `${API_ROUTE}/assignments`,
          method: 'post',
          data: toAssignmentRequest(payload),
        });
        const subscriptionId = toAssignmentRequest(payload).companyBillingSubscriptionId;
        yield put({ type: 'resetAssignModal' });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const viewId = yield select((state) => state[MODEL_NAME].subscriptionView?.subscriptionId);
        if (viewId && String(viewId) === String(subscriptionId)) {
          yield put({ type: 'fetchSubscriptionView', payload: { subscriptionId: viewId } });
        }
      } catch (e) {
        yield put({ type: 'setAssignModal', payload: { submitting: false } });
      }
    },

    *openEnd({ payload }, { put }) {
      yield put({
        type: 'setEndModal',
        payload: { state: true, id: payload?.id, submitting: false },
      });
    },

    *closeEnd(_, { put }) {
      yield put({ type: 'setEndModal', payload: { state: false, id: null, submitting: false } });
    },

    *endAssignment({ payload }, { call, put, select }) {
      const id = payload?.id;
      if (!id) return;
      yield put({ type: 'setEndModal', payload: { submitting: true } });
      try {
        yield call(api.apiRoute, {
          route: `${API_ROUTE}/assignments/${id}/end`,
          method: 'post',
        });
        yield put({ type: 'setEndModal', payload: { state: false, id: null, submitting: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const viewId = yield select((state) => state[MODEL_NAME].subscriptionView?.subscriptionId);
        if (viewId) {
          yield put({ type: 'fetchSubscriptionView', payload: { subscriptionId: viewId } });
        }
      } catch (e) {
        yield put({ type: 'setEndModal', payload: { submitting: false } });
      }
    },

    *fetchSubscriptionView({ payload }, { call, put, select }) {
      const subscriptionId = payload?.subscriptionId;
      if (!subscriptionId) return;
      yield put({
        type: 'setSubscriptionView',
        payload: {
          ...emptySubscriptionView,
          subscriptionId,
          loading: true,
        },
      });

      let assignments = [];
      let nextCharge = null;
      let error = null;
      try {
        const assignmentsRes = yield call(api.getAll, {
          route: `${API_ROUTE}/subscriptions/${subscriptionId}/assignments`,
          readPriority: true,
        });
        assignments = unwrapList(assignmentsRes);
      } catch (e) {
        error = e?.message || null;
      }

      try {
        const chargeRes = yield call(api.getAll, {
          route: `${API_ROUTE}/subscriptions/${subscriptionId}/next-charge`,
          readPriority: true,
        });
        nextCharge = readNextCharge(chargeRes);
      } catch (e) {
        error = error || e?.message || null;
      }

      let payments = [];
      let paymentsError = null;
      try {
        const paymentsRes = yield call(api.getAll, {
          route: `${PAYMENTS_HISTORY_ROUTE}/subscription/${subscriptionId}`,
          readPriority: true,
        });
        payments = unwrapList(paymentsRes).filter((row) => isPaidPayment(row));
      } catch (e) {
        paymentsError = e?.message || null;
      }

      const currentId = yield select((state) => state[MODEL_NAME].subscriptionView?.subscriptionId);
      if (String(currentId) !== String(subscriptionId)) return;
      yield put({
        type: 'setSubscriptionView',
        payload: {
          subscriptionId,
          loading: false,
          assignments,
          nextCharge,
          payments,
          error,
          paymentsError,
        },
      });
    },

    *clearSubscriptionView(_, { put }) {
      yield put({ type: 'setSubscriptionView', payload: emptySubscriptionView });
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    ...makeListReducers({
      initialPagination,
      initialState: moduleInitialState,
    }),
    applyActiveOnly(state, { payload }) {
      return { ...state, activeOnly: payload.activeOnly !== false };
    },
    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        openView: payload.state ? false : state.openView,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
        isConfig: payload.isConfig,
        connectionType: payload.connectionType,
      };
    },
    setDeactivateModal(state, { payload }) {
      return { ...state, deactivateModal: { ...state.deactivateModal, ...payload } };
    },
    setAssignModal(state, { payload }) {
      return { ...state, assignModal: { ...state.assignModal, ...payload } };
    },
    resetAssignModal(state) {
      return { ...state, assignModal: moduleInitialState.assignModal };
    },
    setEndModal(state, { payload }) {
      return { ...state, endModal: { ...state.endModal, ...payload } };
    },
    setSubscriptionView(state, { payload }) {
      return { ...state, subscriptionView: { ...state.subscriptionView, ...payload } };
    },
  },
};

export default CustomerDiscountsModel;
